import React from 'react';
import { SectionGroup } from '@layout';
import { t as translate } from '@utils/translate';

import DesignSystemHero from './DesignSystemHero';
import DesignSystemOverview from './DesignSystemOverview';
import DesignSystemPrinciples from './DesignSystemPrinciples';
import {
  Grid,
  buildGridDemoStrings,
  AccordionDemo,
  Buttons,
  Cards,
  Loaders,
  Forms,
  TabsDemo,
  Stepper,
  CarouselDemo,
  BreadcrumbsDemo,
  CTABandDemo,
  TooltipsDemo,
  OverlayDemo,
  ToastsDemo,
  FileUploaderDemo,
  FileUploaderImageDemo,
  MeterScoreDemo,
  DataTableDemo,
  ImageRotatorDemo,
  ImageSliderDemo,
  CommandPaletteDemo,
  DateRangePickerDemo,
  DatePickerDemo,
  MetricCardsDemo,
  InlineFormValidation,
  ComboBoxDemo,
  PaginationDemo,
  LogicPillsDemo,
  DateInputPopoverDemo,
  FiltersDemo,
  SearchChipsDemo,
  SearchSuggestDemo,
  AsyncInlineValidation,
  MultiStepModalWizardDemo,
  TimelineDemo,
  LogoRotatorDemo,
  ContactFlowComposition,
  DataDashboardComposition,
  EmptyStatesDemo,
} from '../../design-system';

interface DesignSystemComponentsProps {
  isSpanish?: boolean;
}

const DesignSystemComponents = ({ isSpanish = false }: DesignSystemComponentsProps) => {
  const locale = isSpanish ? 'es' : 'en';

  const tStr = (fullKey: string, fallback: string) => {
    const v = translate(fullKey, 'design-system', locale);
    if (typeof v === 'string' && v !== fullKey && v.trim()) return v;
    return fallback;
  };

  const lifecycleRaw = translate(
    'development_process.lifecycle_items',
    'design-system',
    locale
  ) as unknown;
  const stepLabelsFromLifecycle = Array.isArray(lifecycleRaw)
    ? lifecycleRaw
        .map((item) =>
          item && typeof item === 'object' ? (item as { title?: unknown }).title : undefined
        )
        .filter((title): title is string => typeof title === 'string' && title.trim().length > 0)
    : [];

  const stepLabelsFallback = isSpanish
    ? [
        'Descubrimiento y planificacion',
        'Diseno y prototipado',
        'Desarrollo y pruebas',
        'Lanzamiento y soporte',
      ]
    : ['Discovery & Planning', 'Design & Prototyping', 'Development & Testing', 'Launch & Support'];

  const stepLabels = stepLabelsFromLifecycle.length ? stepLabelsFromLifecycle : stepLabelsFallback;

  const gt = (key: string, fallback: string) => tStr(`ui_kit_page.groups.${key}.title`, fallback);
  const gd = (key: string, fallback: string) =>
    tStr(`ui_kit_page.groups.${key}.description`, fallback);
  const workflowEyebrow = tStr('ui_kit_page.groups.workflow.eyebrow', 'System in practice');
  const styleGuideHref = isSpanish ? '/es/style-guide' : '/style-guide';
  const overviewStyleGuideLink = tStr(
    'ui_kit_page.overview.link_style_guide',
    'Full tokens & typography → Style guide'
  );

  const gridStrings = buildGridDemoStrings(locale);

  return (
    <>
      <DesignSystemHero
        sectionID='design-system-hero'
        classes='design-system-hero'
        isSpanish={isSpanish}
      />

      <SectionGroup
        groupId='group-overview'
        groupClassName='section-group'
        title={gt('overview', 'System overview')}
        as='h2'
        description={gd(
          'overview',
          'How the interface behaves: tokens constrain color, type, surfaces, and spacing so composed UI stays consistent.'
        )}
        headingLinkText={overviewStyleGuideLink}
        headingLinkHref={styleGuideHref}
        headingLinkPosition='start'
        buttonLinkText={overviewStyleGuideLink}
        buttonLinkHref={styleGuideHref}
        buttonLinkAs='a'
        buttonLinkClass='btn-primary'
      >
        <DesignSystemOverview sectionID='design-system-overview' isSpanish={isSpanish} />
      </SectionGroup>

      <SectionGroup
        groupId='group-workflow'
        groupClassName='section-group featured-group'
        eyebrow={workflowEyebrow}
        title={gt('workflow', 'Product workflow example')}
        as='h2'
        description={gd(
          'workflow',
          'A real UI flow demonstrating system consistency across interaction patterns.'
        )}
      >
        <Stepper sectionID='stepper' steps={stepLabels} isSpanish={isSpanish} />
      </SectionGroup>

      <SectionGroup
        groupId='group-layout'
        groupClassName='section-group composition-group'
        title={gt('layout', 'Layout & composition')}
        as='h2'
        description={gd(
          'layout',
          'Reusable layout primitives for responsive composition—grids, asides, and card patterns. Visual tokens live in System overview.'
        )}
      >
        <Grid sectionID='grid' classes='grid-demo' strings={gridStrings} />
      </SectionGroup>

      <SectionGroup
        groupId='group-actions'
        groupClassName='section-group'
        title={gt('actions', 'Actions')}
        as='h2'
        description={gd('actions', 'Interactive elements used to trigger user intent.')}
      >
        <Buttons sectionID='buttons' isSpanish={isSpanish} />
        <Loaders sectionID='loaders' isSpanish={isSpanish} />
        <CTABandDemo sectionID='cta-band' isSpanish={isSpanish} />
      </SectionGroup>

      <SectionGroup
        groupId='group-forms'
        groupClassName='section-group featured-group'
        title={gt('forms', 'Core forms & validation')}
        as='h2'
        description={gd(
          'forms',
          'The patterns you reach for first: controls, validation, search, and a composed contact slice—ordered for product relevance.'
        )}
      >
        <>
          <Forms sectionID='forms' cardClassName='surface-4' isSpanish={isSpanish} />
          <InlineFormValidation
            sectionID='inline-form-validation'
            cardClassName='surface-4'
            isSpanish={isSpanish}
          />
          <AsyncInlineValidation
            sectionID='async-inline-validation'
            cardClassName='surface-4'
            isSpanish={isSpanish}
          />
          <SearchChipsDemo sectionID='search-chips' cardClassName='surface-4' isSpanish={isSpanish} />
          <ContactFlowComposition
            sectionID='contact-flow-pattern'
            cardClassName='surface-4'
            isSpanish={isSpanish}
          />
        </>
      </SectionGroup>

      <SectionGroup
        groupId='group-forms-advanced'
        groupClassName='section-group'
        eyebrow={tStr('ui_kit_page.groups.forms_advanced.eyebrow', 'Forms & validation')}
        title={gt('forms_advanced', 'Advanced inputs')}
        as='h2'
        description={gd(
          'forms_advanced',
          'Specialized pickers, uploads, and composite fields—useful for dense tools; keep them behind the core patterns when telling the system story.'
        )}
      >
        <>
          <ComboBoxDemo sectionID='combo-box' className='combo-box-demo' isSpanish={isSpanish} />
          <FileUploaderDemo sectionID='uploader' isSpanish={isSpanish} />
          <FileUploaderImageDemo sectionID='uploader-image' isSpanish={isSpanish} />
          <MeterScoreDemo sectionID='meter' isSpanish={isSpanish} />
          <DateRangePickerDemo sectionID='date-range-picker' isSpanish={isSpanish} />
          <DatePickerDemo sectionID='date-picker' isSpanish={isSpanish} />
          <DateInputPopoverDemo sectionID='date-input-popover' isSpanish={isSpanish} />
        </>
      </SectionGroup>

      <SectionGroup
        groupId='group-navigation'
        groupClassName='section-group'
        title={gt('navigation', 'Navigation & structure')}
        as='h2'
        description={gd(
          'navigation',
          'Wayfinding, pagination, and command-driven shortcuts—tabs and accordion live at the end of this page.'
        )}
      >
        <BreadcrumbsDemo sectionID='breadcrumbs' isSpanish={isSpanish} />
        <PaginationDemo sectionID='pagination' classes='pagination-demo' isSpanish={isSpanish} />
        <CommandPaletteDemo sectionID='cmdk' isSpanish={isSpanish} />
        <SearchSuggestDemo sectionID='search-suggest' isSpanish={isSpanish} />
        <MultiStepModalWizardDemo sectionID='multi-step-modal-wizard' isSpanish={isSpanish} />
      </SectionGroup>

      <SectionGroup
        groupId='group-data'
        groupClassName='section-group'
        title={gt('data', 'Data & feedback')}
        as='h2'
        description={gd(
          'data',
          'Tables, filters, and feedback surfaces—empty, error, and success—so dense UIs stay understandable.'
        )}
      >
        <ToastsDemo sectionID='toasts' isSpanish={isSpanish} />
        <TooltipsDemo sectionID='tooltips' isSpanish={isSpanish} />
        <OverlayDemo sectionID='modals' isSpanish={isSpanish} />
        <EmptyStatesDemo sectionID='empty-states' isSpanish={isSpanish} />
        <DataDashboardComposition sectionID='data-dashboard-pattern' isSpanish={isSpanish} />
        <Cards sectionID='cards' classes='cards-demo' isSpanish={isSpanish} />
        <DataTableDemo sectionID='data-table' isSpanish={isSpanish} />
        <MetricCardsDemo sectionID='metric-cards' isSpanish={isSpanish} />
        <TimelineDemo sectionID='timeline' isSpanish={isSpanish} />
        <FiltersDemo sectionID='filters' isSpanish={isSpanish} />
        <LogicPillsDemo sectionID='logic-pills' isSpanish={isSpanish} />
      </SectionGroup>

      <SectionGroup
        groupId='group-media'
        groupClassName='section-group featured-group no-gap'
        title={gt('media', 'Media & interaction')}
        as='h2'
        description={gd('media', 'Interactive and visual content patterns.')}
      >
        <LogoRotatorDemo sectionID='logo-rotator' classes='surface-3' isSpanish={isSpanish} />
        <CarouselDemo sectionID='carousel' classes='surface-4' isSpanish={isSpanish} />
        <ImageSliderDemo sectionID='image-slider' isSpanish={isSpanish} />
        <ImageRotatorDemo sectionID='image-rotator' isSpanish={isSpanish} />
      </SectionGroup>

      <SectionGroup
        groupId='group-principles'
        groupClassName='section-group'
        title={gt('principles', 'System principles')}
        as='h2'
        description={gd(
          'principles',
          'Guidelines that define how the system is applied across interfaces.'
        )}
      >
        <DesignSystemPrinciples sectionID='design-system-principles' isSpanish={isSpanish} />
      </SectionGroup>

      <SectionGroup
        groupId='group-closing'
        groupClassName='section-group'
        title={gt('closing', 'Built for real products')}
        as='h2'
        description={gd(
          'closing',
          'This system is designed to scale across production environments, balancing performance, accessibility, and usability.'
        )}
      >
        <></>
      </SectionGroup>

      <SectionGroup
        groupId='group-disclosure'
        groupClassName='section-group'
        title={gt('disclosure', 'Tabs & accordion')}
        as='h2'
        description={gd(
          'disclosure',
          'Disclosure patterns placed here so the workflow example stays the primary narrative for multi-step content.'
        )}
      >
        <TabsDemo sectionID='tabs' isSpanish={isSpanish} />
        <AccordionDemo sectionID='accordion' isSpanish={isSpanish} />
      </SectionGroup>
    </>
  );
};

export default DesignSystemComponents;
